import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button } from "react-bootstrap";
import PlaygroundUsageService, {
  PlaygroundFilters,
  PlaygroundOverview,
  TopActiveStep,
} from "../../_services/PlaygroundUsageService";
import "./learning-analytics-hub.css";

type SharedFilters = PlaygroundFilters;

interface CrossRef {
  label: string;
  hubKey: string;
}

interface Tile {
  key: string;
  emoji: string;
  title: string;
  description: string;
  /** route path; undefined => contextual-only (no direct launch) */
  route?: string;
  /** how the tile becomes accessible if not directly launchable */
  contextHint?: string;
  /** related tile keys */
  xrefs?: CrossRef[];
}

interface Section {
  title: string;
  tiles: Tile[];
}

const SECTIONS: Section[] = [
  {
    title: "Student Practice",
    tiles: [
      {
        key: "playground-usage",
        emoji: "\u{1F3AE}",
        title: "Playground Usage",
        description:
          "Which playground assets (subjects, resources, related quizzes) students are using, how often, and by whom. Drill into per-student or per-asset views.",
        route: "/playground-usage",
        xrefs: [
          { label: "Pathway Analytics", hubKey: "pathway-analytics" },
          { label: "Doubts", hubKey: "doubts-dashboard" },
        ],
      },
      {
        key: "pathway-analytics",
        emoji: "\u{1F9ED}",
        title: "Pathway Analytics",
        description:
          "Interactive Pathway submissions — chosen pathways, AI scoring, justifications, time-on-task across students.",
        route: "/pathway-analytics",
        xrefs: [
          { label: "Playground Usage", hubKey: "playground-usage" },
          { label: "Step Analytics", hubKey: "step-analytics" },
        ],
      },
      {
        key: "doubts-dashboard",
        emoji: "\u{1F4AC}",
        title: "Doubts Dashboard",
        description:
          "Student-raised doubts — volume by topic, by batch, response SLA. Useful next to playground practice to see where students are getting stuck.",
        route: "/doubts-dashboard",
        xrefs: [
          { label: "Playground Usage", hubKey: "playground-usage" },
          { label: "Class Review", hubKey: "class-review-dashboard" },
        ],
      },
    ],
  },
  {
    title: "Flip-Class",
    tiles: [
      {
        key: "executive-flip-dashboard",
        emoji: "\u{1F4CA}",
        title: "Executive Flip Dashboard",
        description:
          "Institution-level flip-class adoption, pre-/in-/post-class completion, learning effectiveness, at-risk batches with school → course → batch drill-down.",
        route: "/executive-flip-dashboard",
        xrefs: [
          { label: "Exec Engagement", hubKey: "exec-flip" },
          { label: "Step Activity", hubKey: "step-analytics" },
        ],
      },
      {
        key: "exec-flip",
        emoji: "\u{1F525}",
        title: "Flip Engagement",
        description:
          "Real-time lesson plan engagement and step-level performance for a selected batch.",
        route: "/exec-flip",
        xrefs: [
          { label: "Class Review", hubKey: "class-review-dashboard" },
          { label: "Step Activity", hubKey: "step-analytics" },
        ],
      },
      {
        key: "step-analytics",
        emoji: "\u{1F3AF}",
        title: "Step Activity",
        description:
          "Per-step student engagement: resource views, time spent, completion, revisits, test scores.",
        contextHint: "Click any row above to launch.",
        xrefs: [
          { label: "Class Review", hubKey: "class-review-dashboard" },
          { label: "Flip Engagement", hubKey: "exec-flip" },
        ],
      },
      {
        key: "class-review-dashboard",
        emoji: "\u{1F4DA}",
        title: "Class Review",
        description:
          "Step-level class performance review used during/after a session.",
        contextHint:
          "Opened from a step inside a lesson plan — needs lessonPlanId and stepId.",
        xrefs: [
          { label: "Step Activity", hubKey: "step-analytics" },
          { label: "Doubts", hubKey: "doubts-dashboard" },
        ],
      },
    ],
  },
  {
    title: "LMS & Career",
    tiles: [
      {
        key: "atlas-usage",
        emoji: "\u{1F30D}",
        title: "Atlas Usage",
        description:
          "Career-guidance (Atlas) track engagement — which learners opened which tracks, day steps and resources, progress and time spent, with per-track drill-down.",
        route: "/atlas-usage",
        xrefs: [
          { label: "Playground Usage", hubKey: "playground-usage" },
          { label: "LMS Analytics", hubKey: "lms-analytics" },
        ],
      },
      {
        key: "lms-analytics",
        emoji: "\u{1F4D8}",
        title: "LMS Analytics",
        description:
          "Cross-cutting LMS resource and engagement metrics across learners and programs.",
        route: "/lms-analytics",
        xrefs: [{ label: "Atlas Usage", hubKey: "atlas-usage" }],
      },
    ],
  },
];

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const fmtNum = (n: number | null | undefined) =>
  n === null || n === undefined ? "—" : (n || 0).toLocaleString();

const buildQuery = (f: SharedFilters): string => {
  const params = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") params.append(k, String(v));
  });
  const qs = params.toString();
  return qs ? `?${qs}` : "";
};

const LearningAnalyticsHub: React.FC = () => {
  const history = useHistory();
  const [filters, setFilters] = useState<SharedFilters>({
    startDate: daysAgo(30),
    endDate: today(),
  });
  const [pgOverview, setPgOverview] = useState<PlaygroundOverview | null>(null);
  const [pgLoading, setPgLoading] = useState(false);
  const [topSteps, setTopSteps] = useState<TopActiveStep[]>([]);
  const [stepsLoading, setStepsLoading] = useState(false);

  // Index tiles by key for cross-ref scrolling
  const tilesByKey = useMemo(() => {
    const map: Record<string, Tile> = {};
    SECTIONS.forEach((s) => s.tiles.forEach((t) => (map[t.key] = t)));
    return map;
  }, []);

  useEffect(() => {
    let cancelled = false;
    setPgLoading(true);
    PlaygroundUsageService.getOverview(filters)
      .then((d) => {
        if (!cancelled) setPgOverview(d);
      })
      .catch(() => {
        if (!cancelled) setPgOverview(null);
      })
      .finally(() => {
        if (!cancelled) setPgLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters]);

  useEffect(() => {
    let cancelled = false;
    setStepsLoading(true);
    PlaygroundUsageService.getTopActiveSteps({
      days: 7,
      limit: 6,
      batchId: filters.batchId,
      courseId: filters.courseId,
    })
      .then((d) => {
        if (!cancelled) setTopSteps(d.steps || []);
      })
      .catch(() => {
        if (!cancelled) setTopSteps([]);
      })
      .finally(() => {
        if (!cancelled) setStepsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters.batchId, filters.courseId]);

  const navigateTo = (tile: Tile) => {
    if (!tile.route) return;
    history.push(`${tile.route}${buildQuery(filters)}`);
  };

  const scrollToTile = (key: string) => {
    const el = document.getElementById(`lah-tile-${key}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.style.transition = "box-shadow 0.3s";
      el.style.boxShadow = "0 0 0 3px #818cf8";
      setTimeout(() => {
        el.style.boxShadow = "";
      }, 1200);
    }
  };

  const renderKpis = (tile: Tile) => {
    if (tile.key !== "playground-usage") return null;
    return (
      <div className={`lah-kpis ${pgOverview ? "" : "is-empty"}`}>
        <div className="lah-kpi">
          <div className="lah-kpi-val">
            {pgLoading ? "…" : fmtNum(pgOverview?.distinctStudents)}
          </div>
          <div className="lah-kpi-lbl">Students</div>
        </div>
        <div className="lah-kpi">
          <div className="lah-kpi-val">
            {pgLoading ? "…" : fmtNum(pgOverview?.totalEvents)}
          </div>
          <div className="lah-kpi-lbl">Events</div>
        </div>
        <div className="lah-kpi">
          <div className="lah-kpi-val">
            {pgLoading ? "…" : fmtNum(pgOverview?.totalAttempts)}
          </div>
          <div className="lah-kpi-lbl">Attempts</div>
        </div>
      </div>
    );
  };

  const launchStep = (s: TopActiveStep) => {
    const params = new URLSearchParams({
      lessonPlanId: String(s.lessonPlanId),
      stepId: String(s.stepId),
      batchId: String(s.batchId),
      stepName: s.stepName || "",
    });
    history.push(`/step-analytics?${params.toString()}`);
  };

  const renderInlineList = (tile: Tile) => {
    if (tile.key !== "step-analytics") return null;
    return (
      <div className="lah-inline-list">
        <div className="lah-inline-head">
          <span>Recently active (last 7 days)</span>
          {stepsLoading && <span className="lah-inline-loading">…</span>}
        </div>
        {!stepsLoading && topSteps.length === 0 && (
          <div className="lah-inline-empty">
            No step engagement in this window yet.
          </div>
        )}
        {topSteps.map((s) => (
          <button
            type="button"
            key={`${s.stepId}-${s.batchId}`}
            className="lah-inline-row"
            onClick={() => launchStep(s)}
            title={`${s.lessonPlanName} · ${s.batchName}`}
          >
            <div className="lah-inline-row-main">
              <span className="lah-inline-step">{s.stepName}</span>
              <span className="lah-inline-meta">
                {s.lessonPlanName} · {s.batchName}
              </span>
            </div>
            <div className="lah-inline-row-stats">
              <span>{fmtNum(s.eventCount)} evts</span>
              <span className="lah-inline-sep">·</span>
              <span>{fmtNum(s.distinctStudents)} stu</span>
            </div>
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="lah">
      <div className="lah-header">
        <h1 className="lah-title">Learning Workspace</h1>
        <p className="lah-subtitle">
          Every learning-side dashboard — student practice, flip-class engagement,
          Atlas, LMS content. Set a date window and scope below, then open any
          report; your filters flow through. For exec, AI-governance, and finance
          reports, go to the{" "}
          <button
            type="button"
            onClick={() => history.push("/analytics")}
            style={{ background: "none", border: "none", padding: 0, color: "var(--brand-primary)", textDecoration: "underline", cursor: "pointer", font: "inherit" }}
          >
            Reports Directory
          </button>.
        </p>
      </div>

      <div className="lah-filters">
        <div className="field">
          <label>Start Date</label>
          <input
            type="date"
            value={filters.startDate || ""}
            onChange={(e) =>
              setFilters((p) => ({ ...p, startDate: e.target.value }))
            }
          />
        </div>
        <div className="field">
          <label>End Date</label>
          <input
            type="date"
            value={filters.endDate || ""}
            onChange={(e) =>
              setFilters((p) => ({ ...p, endDate: e.target.value }))
            }
          />
        </div>
        <div className="field">
          <label>Batch ID</label>
          <input
            type="number"
            placeholder="Optional"
            value={filters.batchId ?? ""}
            onChange={(e) =>
              setFilters((p) => ({
                ...p,
                batchId: e.target.value ? Number(e.target.value) : undefined,
              }))
            }
          />
        </div>
        <div className="field">
          <label>Course ID</label>
          <input
            type="number"
            placeholder="Optional"
            value={filters.courseId ?? ""}
            onChange={(e) =>
              setFilters((p) => ({
                ...p,
                courseId: e.target.value ? Number(e.target.value) : undefined,
              }))
            }
          />
        </div>
        <div className="hint">
          Filters pass through to each dashboard via URL.
        </div>
      </div>

      {SECTIONS.map((section) => (
        <div className="lah-section" key={section.title}>
          <h2 className="lah-section-title">{section.title}</h2>
          <div className="lah-tiles">
            {section.tiles.map((tile) => {
              const launchable = Boolean(tile.route);
              return (
                <div
                  className={`lah-tile ${launchable ? "is-launchable" : ""}`}
                  id={`lah-tile-${tile.key}`}
                  key={tile.key}
                >
                  <div className="lah-tile-head">
                    <div className="lah-tile-emoji" aria-hidden="true">
                      {tile.emoji}
                    </div>
                    <h3 className="lah-tile-title">{tile.title}</h3>
                  </div>
                  <p className="lah-tile-desc">{tile.description}</p>
                  {renderKpis(tile)}
                  {renderInlineList(tile)}
                  <div className="lah-tile-actions">
                    {launchable ? (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => navigateTo(tile)}
                      >
                        Open dashboard →
                      </Button>
                    ) : (
                      <span className="lah-tile-status">
                        {tile.contextHint ||
                          "Opened in-context from another page."}
                      </span>
                    )}
                  </div>
                  {tile.xrefs && tile.xrefs.length > 0 && (
                    <div className="lah-xrefs">
                      <span className="lah-xrefs-label">Related:</span>
                      {tile.xrefs.map((x) => (
                        <a
                          key={x.hubKey}
                          className="lah-xref-chip"
                          onClick={(e) => {
                            e.preventDefault();
                            const target = tilesByKey[x.hubKey];
                            if (!target) return;
                            if (target.route) {
                              history.push(
                                `${target.route}${buildQuery(filters)}`,
                              );
                            } else {
                              scrollToTile(x.hubKey);
                            }
                          }}
                          href="#"
                        >
                          {x.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default LearningAnalyticsHub;
